<template>
  <div class="escalations-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="page-title">Escalations</h1>
              <p class="page-subtitle">Monitor and manage call escalations from customer interactions</p>
            </div>
          </div>
        </div>

        <!-- Statistics Cards -->
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon danger">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ totalEscalations }}</div>
              <div class="stat-label">Total Escalations</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ highLevelEscalations }}</div>
              <div class="stat-label">High Level (3+)</div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ categoriesAffected.length }}</div>
              <div class="stat-label">Categories Affected</div>
            </div>
          </div>

        </div>

        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-header">
            <h3>Filter Escalations</h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Agent</label>
              <select v-model="filters.agentId" @change="applyFilters" class="filter-select">
                <option value="">All Agents</option>
                <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Escalation Level</label>
              <select v-model="filters.escalationLevel" @change="applyFilters" class="filter-select">
                <option value="">All Levels</option>
                <option value="1">Level 1 - Low</option>
                <option value="2">Level 2 - Medium</option>
                <option value="3">Level 3 - High</option>
                <option value="4">Level 4 - Critical</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Category</label>
              <select v-model="filters.category" @change="applyFilters" class="filter-select">
                <option value="">All Categories</option>
                <option value="insult">Insults</option>
                <option value="profanity">Profanity</option>
                <option value="legal_threat">Legal Threats</option>
                <option value="social_media_threat">Social Media Threats</option>
                <option value="reputation_threat">Reputation Threats</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Date Range</label>
              <div class="date-range">
                <input
                  v-model="filters.dateFrom"
                  @change="applyFilters"
                  type="date"
                  class="filter-input"
                  placeholder="From"
                />
                <span class="date-separator">to</span>
                <input
                  v-model="filters.dateTo"
                  @change="applyFilters"
                  type="date"
                  class="filter-input"
                  placeholder="To"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Escalations Table -->
        <div class="escalations-section">
          <div class="section-header">
            <h3>Escalation Records</h3>
            <div class="table-controls">
              <div class="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <input
                  v-model="searchTerm"
                  @input="searchEscalations"
                  type="text"
                  placeholder="Search by customer phone or text..."
                  class="search-input"
                />
              </div>
            </div>
          </div>

          <div class="table-container">
            <table class="escalations-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Agent</th>
                  <th>Customer</th>
                  <th>Level</th>
                  <th>Categories</th>
                  <th>Violations</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="escalation in escalations" :key="escalation.id" class="escalation-row">
                  <td class="escalation-id">#{{ escalation.id }}</td>
                  <td class="agent-info">
                    <div class="agent-name">{{ escalation.agent_name }}</div>
                    <div class="agent-id">ID: {{ escalation.agent_id }}</div>
                  </td>
                  <td class="customer-phone">{{ escalation.customer_phone }}</td>
                  <td class="escalation-level">
                    <span :class="['level-badge', `level-${escalation.escalation_level}`]">
                      Level {{ escalation.escalation_level }}
                    </span>
                  </td>
                  <td class="categories">
                    <div class="category-tags">
                      <span
                        v-for="category in getUniqueCategories(escalation.categories_affected)"
                        :key="category"
                        :class="['category-tag', getCategoryClass(category)]"
                      >
                        {{ formatCategory(category) }}
                      </span>
                    </div>
                  </td>
                  <td class="violations-count">
                    <span class="violations-badge">{{ escalation.total_violations }}</span>
                  </td>
                  <td class="escalation-date">
                    <div class="date-time">
                      <div class="date">{{ formatDate(escalation.created_at) }}</div>
                      <div class="time">{{ formatTime(escalation.created_at) }}</div>
                    </div>
                  </td>
                  <td class="actions">
                    <button @click="viewEscalation(escalation)" class="action-btn view-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Loading escalations...</p>
            </div>

            <!-- Empty State -->
            <div v-if="!loading && escalations.length === 0" class="empty-state">
              <div class="empty-icon">📞</div>
              <h3>No escalations found</h3>
              <p>No escalation records match your current filters.</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.total_pages > 1" class="pagination">
            <button
              @click="goToPage(pagination.current_page - 1)"
              :disabled="!pagination.has_prev_page"
              class="pagination-btn"
            >
              Previous
            </button>

            <div class="pagination-info">
              Page {{ pagination.current_page }} of {{ pagination.total_pages }}
              ({{ pagination.total_count }} total)
            </div>

            <button
              @click="goToPage(pagination.current_page + 1)"
              :disabled="!pagination.has_next_page"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>

        <!-- Escalation Detail Modal -->
        <EscalationDetailModal
          :visible="showDetailModal"
          :escalation="selectedEscalation"
          @close="showDetailModal = false"
        />
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import EscalationDetailModal from '../components/EscalationDetailModal.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'EscalationsView',
  components: {
    Sidebar,
    TopHeader,
    EscalationDetailModal
  },
  data () {
    return {
      escalations: [],
      agents: [],
      pagination: {},
      loading: false,
      searchTerm: '',
      filters: {
        agentId: '',
        escalationLevel: '',
        category: '',
        dateFrom: '',
        dateTo: ''
      },
      showDetailModal: false,
      selectedEscalation: null
    }
  },
  computed: {
    totalEscalations () {
      return this.pagination.total_count || 0
    },
    highLevelEscalations () {
      return this.escalations.filter(e => e.escalation_level >= 3).length
    },
    categoriesAffected () {
      const categories = new Set()
      this.escalations.forEach(escalation => {
        escalation.detected_words.forEach(word => {
          categories.add(word.category)
        })
      })
      return Array.from(categories)
    }
  },
  async mounted () {
    await Promise.all([
      this.loadEscalations(),
      this.loadAgents()
    ])
  },
  methods: {
    async loadEscalations (page = 1) {
      try {
        this.loading = true
        const params = {
          page,
          ...this.filters
        }

        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        const result = await DashboardService.getEscalations(params)

        if (result.success) {
          this.escalations = result.escalations
          this.pagination = result.pagination
        }
      } catch (error) {
        console.error('Error loading escalations:', error)
        this.escalations = []
      } finally {
        this.loading = false
      }
    },

    async loadAgents () {
      try {
        const customerServiceAgents = await DashboardService.getAgents(1)
        const salesAgents = await DashboardService.getAgents(2)
        this.agents = [...customerServiceAgents, ...salesAgents]
      } catch (error) {
        console.error('Error loading agents:', error)
        this.agents = []
      }
    },

    applyFilters () {
      this.loadEscalations(1)
    },

    searchEscalations () {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadEscalations(1)
      }, 500)
    },

    goToPage (page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.loadEscalations(page)
      }
    },

    viewEscalation (escalation) {
      this.selectedEscalation = escalation
      this.showDetailModal = true
    },

    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },

    formatTime (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    formatCategory (category) {
      const categoryMap = {
        insult: 'Insults',
        profanity: 'Profanity',
        legal_threat: 'Legal Threats',
        social_media_threat: 'Social Media',
        reputation_threat: 'Reputation'
      }
      return categoryMap[category] || category
    },

    getCategoryClass (category) {
      const classMap = {
        insult: 'insult',
        profanity: 'profanity',
        legal_threat: 'legal',
        social_media_threat: 'social',
        reputation_threat: 'reputation'
      }
      return classMap[category] || 'default'
    },

    getUniqueCategories (categories) {
      if (Array.isArray(categories)) {
        return categories
      }

      // Handle object format like {"0": "insult", "2": "legal_threat"}
      if (typeof categories === 'object' && categories !== null) {
        return Object.values(categories)
      }

      return []
    }
  }
}
</script>

<style scoped>
.escalations-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

.title-section h1 {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: all 0.2s;
}

.stat-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px var(--shadow-primary);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-icon.danger { background: linear-gradient(135deg, #ef4444, #dc2626); }
.stat-icon.warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
.stat-icon.info { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.stat-icon.success { background: linear-gradient(135deg, #10b981, #059669); }

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.filters-section {
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  margin-bottom: 32px;
}

.filters-header {
  margin-bottom: 20px;
}

.filters-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.filter-select,
.filter-input {
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.date-range {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-separator {
  font-size: 14px;
  color: var(--text-secondary);
}

.escalations-section {
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.section-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box svg {
  position: absolute;
  left: 12px;
  color: var(--text-secondary);
}

.search-input {
  padding: 10px 12px 10px 40px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  width: 300px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.table-container {
  overflow-x: auto;
}

.escalations-table {
  width: 100%;
  border-collapse: collapse;
}

.escalations-table th {
  padding: 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-primary);
}

.escalations-table td {
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  vertical-align: top;
}

.escalation-row:hover {
  background: var(--bg-secondary);
}

.escalation-id {
  font-family: monospace;
  font-weight: 600;
  color: var(--text-secondary);
}

.agent-info {
  min-width: 140px;
}

.agent-name {
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.agent-id {
  font-size: 12px;
  color: var(--text-secondary);
}

.customer-phone {
  font-family: monospace;
  color: var(--text-primary);
}

.level-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.level-badge.level-1 { background: #10b981; }
.level-badge.level-2 { background: #f59e0b; }
.level-badge.level-3 { background: #ef4444; }
.level-badge.level-4 { background: #dc2626; }

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 200px;
}

.category-tag {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  color: white;
}

.category-tag.insult { background: #ef4444; }
.category-tag.profanity { background: #dc2626; }
.category-tag.legal { background: #7c3aed; }
.category-tag.social { background: #3b82f6; }
.category-tag.reputation { background: #f59e0b; }
.category-tag.default { background: #6b7280; }

.violations-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: #f59e0b;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
}

.date-time {
  min-width: 100px;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.time {
  font-size: 12px;
  color: var(--text-secondary);
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.view-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid var(--border-primary);
}

.pagination-btn {
  padding: 8px 16px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.pagination-info {
  font-size: 14px;
  color: var(--text-secondary);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dark mode specific styles */
.dark .filter-select option {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .search-input {
    width: 100%;
  }

  .table-container {
    font-size: 14px;
  }

  .escalations-table th,
  .escalations-table td {
    padding: 12px 8px;
  }

  .category-tags {
    max-width: none;
  }
}
</style>
